import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import BubbleCommon from '@op/components/BubbleCommon';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';

import DataForm from './DataForm';
import useLanguage from '@op/hooks/useLanguage';
import { useFontLoading } from '@op/hooks/common';
import { connect } from 'umi';
import { Button } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { DBO_U } from '@op/utils/format';
import ExecutionAnalysis from './ExecutionAnalysis';
import ExecutionProgress from './ExecutionProgress';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';

interface ModelContentProps {
  gameInfo: any;
  wholeGameInfo: any;
  moduleName: string;
  moduleId: number;
  moduleKeyWords: any;
  filter: any;
  sortLoading?: boolean;
  tags: Array<any>;
  feedsFilter: any;
  condLoading: boolean;
}

function ModelContent({
  gameInfo,
  moduleName,
  moduleId,
  moduleKeyWords,
  filter: oriFilter,
  sortLoading,
  tags,
  condLoading,
  feedsFilter,
  wholeGameInfo,
}: ModelContentProps) {
  const { formatMessage } = useLanguage();
  const [classify, setClassify] = useState('all');
  const { loading, changeLoading } = useFontLoading();
  const HIDE_TAG = useFrontWebConfig({
    keys: 'hide_weekly_report_tags',
  });

  const { filter, summaryFilter, videoFilter } = useMemo(() => {
    if (!oriFilter) return {};
    const tagName = classify === 'all' ? '' : classify;
    const filter = {
      ...oriFilter,
      tag_name: tagName,
      module_id: moduleId,
    };
    const summaryFilter = {
      ...filter,
      game_id: gameInfo.edition_unified_id,
      module_name: moduleName,
    };
    return {
      filter,
      summaryFilter,
      videoFilter: {
        ...summaryFilter,
        keywords: moduleKeyWords,
      },
    };
  }, [classify]);

  const implementFilter = useMemo(() => {
    return {
      ...oriFilter,
      module_id: moduleId,
      game_id: gameInfo.edition_unified_id,
    };
  }, []);

  const tagsOptions = useMemo(() => {
    if ((tags || []).length === 0) return [];

    const list = tags.map((item) => ({
      text: item.tag_name,
      value: item.tag_name,
    }));

    return [{ text: formatMessage('All'), value: 'all' }, ...list];
  }, [formatMessage]);

  return (
    <CardCommon
      id={moduleId}
      style={{ scrollMarginTop: 60 }}
      title={
        <div className="dbo-card-title-info">
          <div className="dbo-card-title-info-title">{moduleName}</div>
          <div style={{ marginLeft: 10 }} className="dbo-card-title-info-desc">
            {formatMessage('Keywords')}
          </div>
          <BubbleCommon
            placement="top"
            iconStyle={{ marginLeft: 5, marginRight: 10 }}
            tipsMap={null}
            content={moduleKeyWords.flat(Infinity).join(', ')}
          ></BubbleCommon>
          {tagsOptions.length > 0 && !HIDE_TAG && (
            <SortBySelect
              defaultValue={classify}
              onChange={(key: string, value: any) => {
                setClassify(value);
                changeLoading();
              }}
              defaultParams={{
                options: tagsOptions,
                needFormatMessage: false,
                btnTextPrefix: formatMessage('Category'),
                matchButtonWidth: true,
                dealwithButton: (value: string | number, { textMap }: any) =>
                  textMap?.[value] || value,
              }}
            />
          )}
        </div>
      }
      alwaysShowOperations
      dropdownsFilterBeforeElement={
        <>
          {!condLoading && (
            <Button
              onClick={() => {
                const keyword = moduleKeyWords.flat(Infinity).join(';');
                DBO_U.go({
                  path: `/opinion/Feeds/Feeds`,
                  params: {
                    ...filter,
                    review_types: [
                      'posts_text',
                      'posts_video',
                      'stream_titles',
                    ],
                    channel: ['twitter', 'youtube', 'instagram', 'tiktok'],
                    channel_type: 'social',
                    external_data_sources: ['-99999'],
                    social_media_by_account: ['99999'],
                    keywordCustomInput: keyword,
                    social_media_by_keyword: ['99999'],
                    ...feedsFilter,
                  },
                });
              }}
              type="link"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {formatMessage('View Related Original Comments')}{' '}
              <SvgIcon type="arrow-right" className="card-slot-icon" />
            </Button>
          )}
        </>
      }
      loadingHeight={1257}
      loading={loading || sortLoading}
    >
      {/* 数据表模块 */}
      <DataForm gameInfo={gameInfo} filter={filter}></DataForm>

      {/* 洞察分析模块 */}
      <ExecutionProgress
        gameInfo={gameInfo}
        wholeGameInfo={wholeGameInfo}
        filter={summaryFilter}
        videoFilter={videoFilter}
      ></ExecutionProgress>

      {/* 执行进度模块 */}
      <div className="dbo-section-execution">
        <ExecutionAnalysis filter={implementFilter}></ExecutionAnalysis>
      </div>
    </CardCommon>
  );
}

export default connect((state: any, props: ModelContentProps) => {
  const { opinionCommonOrderLoading } = state;
  const { moduleId } = props;
  const { couldLoading } = opinionCommonOrderLoading.modules?.[moduleId] || {
    couldLoading: false,
  };
  return {
    sortLoading: !couldLoading,
  };
})(ModelContent);
